import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';

const CreativeCards = ({ client }) => {
    const [cardDeck, setCardDeck] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [reflection, setReflection] = useState('');
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [customCards, setCustomCards] = useState([]); // Carte caricate dall'utente

    // Card prompts Dixit-style (evocativi, metaforici)
    const cardPrompts = [
        { id: 1, theme: "Il Viaggio", image: "/cards/card_viaggio_1768924730651.png", prompt: "Isole fluttuanti" },
        { id: 2, theme: "L'Apertura", image: "/cards/card_apertura_1768924746295.png", prompt: "Porta cosmica" },
        { id: 3, theme: "La Liberazione", image: "/cards/card_liberazione_1768924762616.png", prompt: "Farfalle di luce" },
        { id: 4, theme: "Il Labirinto", image: "/cards/card_labirinto_1768924778580.png", prompt: "Specchi interiori" },
        { id: 5, theme: "La Rinascita", image: "/cards/card_rinascita_1768924799650.png", prompt: "Fenice di fuoco" },
        { id: 6, theme: "Il Ponte", image: "/cards/card_ponte_1768924818719.png", prompt: "Connessione cosmica" },
        { id: 7, theme: "Le Radici", image: "/cards/card_radici_1768924836165.png", prompt: "Albero delle stelle" },
        { id: 8, theme: "Il Riflesso", image: "/cards/card_riflesso_1768924854559.png", prompt: "Lago emotivo" },
        { id: 9, theme: "La Chiave", image: "/cards/card_chiave_1768924877611.png", prompt: "Potenziale sbloccato" },
        { id: 10, theme: "La Trasformazione", image: "/cards/card_trasformazione_1768924903800.png", prompt: "Crisalide luminosa" },
        { id: 11, theme: "Il Vuoto", image: "/cards/card_vuoto_1768924928300.png", prompt: "Cornice vuota" },
        { id: 12, theme: "La Connessione", image: "/cards/card_connessione_1768924953900.png", prompt: "Fili di luce" }
    ];

    // Carica carte personalizzate dal client
    useEffect(() => {
        const loadCustomCards = async () => {
            if (client?.customCreativeCards) {
                setCustomCards(client.customCreativeCards);
            }
        };
        loadCustomCards();
    }, [client]);

    // Funzione per caricare una nuova immagine
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Verifica che sia un'immagine
        if (!file.type.startsWith('image/')) {
            alert('⚠️ Carica solo file immagine (JPG, PNG, ecc.)');
            return;
        }

        // Verifica dimensione file (max 1MB per evitare problemi con Firebase)
        if (file.size > 1024 * 1024) {
            alert('⚠️ L\'immagine è troppo grande. Carica un\'immagine più piccola di 1MB');
            return;
        }

        // Converti l'immagine in base64
        const reader = new FileReader();
        reader.onload = async (e) => {
            const imageBase64 = e.target.result;

            // Chiedi tema e prompt
            const theme = prompt('Inserisci il tema della carta:', 'La mia carta');
            if (!theme) return;

            const promptText = prompt('Inserisci il prompt evocativo:', '');

            // Crea la nuova carta come oggetto JSON puro
            const newCard = {
                id: `custom_${Date.now()}`,
                theme: String(theme),
                image: String(imageBase64),
                prompt: String(promptText || 'Carta personalizzata'),
                custom: true
            };

            // Aggiungi alle carte personalizzate
            const updatedCustomCards = [...customCards, newCard];

            // Serializza e de-serializza per assicurarsi che sia JSON puro
            const cleanCards = JSON.parse(JSON.stringify(updatedCustomCards));

            setCustomCards(cleanCards);

            // Salva nel database
            try {
                await DataManager.updateClient(client.id, {
                    customCreativeCards: cleanCards
                });
                alert('✅ Carta caricata con successo!');
                // Reset input
                event.target.value = '';
            } catch (error) {
                console.error('Errore nel salvataggio:', error);
                alert('❌ Errore nel salvataggio della carta: ' + error.message);
            }
        };

        reader.readAsDataURL(file);
    };

    // Combina carte predefinite e personalizzate
    const allCards = [...cardPrompts, ...customCards];

    const shuffleCards = () => {
        const shuffled = [...allCards].sort(() => 0.5 - Math.random());
        const newDeck = shuffled.slice(0, 3);
        setCardDeck(newDeck);
        setSelectedCard(null);
        setReflection('');
    };

    useEffect(() => {
        if (allCards.length > 0) {
            shuffleCards();
        }
    }, [allCards.length]); // Rimischia quando cambia il numero di carte disponibili

    // Close fullscreen on ESC key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && fullscreenImage) {
                setFullscreenImage(null);
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [fullscreenImage]);

    const handleSaveReflection = async () => {
        if (!selectedCard) {
            alert('⚠️ Seleziona prima una carta');
            return;
        }

        if (!reflection.trim()) {
            alert('⚠️ Inserisci una riflessione');
            return;
        }

        try {
            await DataManager.saveCreativeWork({
                clientId: client.id,
                type: 'creative_cards_session',
                content: {
                    cardId: selectedCard.id,
                    cardTheme: selectedCard.theme,
                    cardPrompt: selectedCard.prompt,
                    reflection: reflection.trim(),
                    date: new Date().toISOString()
                },
                shared: true
            });

            alert(`✅ Riflessione salvata per: ${selectedCard.theme}`);
            setReflection('');
            setSelectedCard(null);
        } catch (error) {
            console.error(error);
            alert("Errore durante il salvataggio.");
        }
    };

    const handleImageClick = (e, card) => {
        e.stopPropagation(); // Prevent double event
        setSelectedCard(card); // Select the card
        setFullscreenImage(card); // Open fullscreen
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6">
                <div className="flex items-start gap-4">
                    <span className="text-5xl grayscale">🎴</span>
                    <div className="flex-1">
                        <h2 className="text-2xl font-bold text-slate-800 mb-2 font-sans">Carte Creative</h2>
                        <p className="text-slate-600 font-sans">
                            Strumento di riflessione emotiva ispirato a Dixit. Le carte evocative
                            aiutano a esprimere emozioni difficili da verbalizzare.
                        </p>
                    </div>
                </div>
            </div>

            {/* Action: Shuffle and Upload */}
            <div className="flex justify-between items-center">
                <button
                    onClick={shuffleCards}
                    className="px-6 py-3 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition font-sans border-0"
                >
                    🔀 Mischia Carte
                </button>

                <div className="flex items-center gap-3">
                    {customCards.length > 0 && (
                        <span className="text-sm text-slate-600 font-sans">
                            {customCards.length} carta{customCards.length !== 1 ? 'e' : ''} personalizzata{customCards.length !== 1 ? 'e' : ''}
                        </span>
                    )}
                    <label className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition font-sans border-0 cursor-pointer">
                        📤 Carica Nuova Carta
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                        />
                    </label>
                </div>
            </div>

            {/* Card Grid - 3 Cards */}
            {cardDeck.length > 0 && (
                <div className="grid md:grid-cols-3 gap-6">
                    {cardDeck.map((card, index) => (
                        <div
                            key={card.id}
                            onClick={() => setSelectedCard(card)}
                            className={`bg-white rounded-2xl border-2 p-4 space-y-3 cursor-pointer transition-all ${selectedCard?.id === card.id
                                ? 'border-slate-900 shadow-lg scale-105'
                                : 'border-slate-200 hover:border-slate-400'
                                }`}
                        >
                            {/* Card Image - Click to Fullscreen */}
                            <div
                                className="relative bg-slate-100 rounded-xl overflow-hidden aspect-[2/3] cursor-zoom-in"
                                onClick={(e) => handleImageClick(e, card)}
                            >
                                <img
                                    src={card.image}
                                    alt={card.theme}
                                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                                />
                                {/* Badge per carte personalizzate */}
                                {card.custom && (
                                    <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded-lg text-[10px] font-bold font-sans shadow-lg">
                                        Personalizzata
                                    </div>
                                )}
                            </div>

                            {/* Selection Indicator */}
                            {selectedCard?.id === card.id && (
                                <div className="bg-slate-900 text-white text-center py-2 rounded-lg text-xs font-bold font-sans">
                                    ✓ Selezionata
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Reflection Box - Always Below */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="mb-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2 font-sans">
                        💭 Riflessione sulla carta selezionata
                    </label>
                    {selectedCard ? (
                        <div className="text-xs text-slate-500 mb-3 font-sans">
                            Stai commentando: <strong className="text-slate-900">Carta #{selectedCard.id} - {selectedCard.theme}</strong>
                        </div>
                    ) : (
                        <div className="text-xs text-slate-400 mb-3 italic font-sans">
                            Seleziona prima una carta per iniziare a scrivere
                        </div>
                    )}
                </div>

                <textarea
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    disabled={!selectedCard}
                    className="w-full p-4 border-2 border-slate-200 rounded-xl focus:border-slate-900 focus:outline-none resize-none text-sm font-sans disabled:bg-slate-50 disabled:cursor-not-allowed"
                    rows="6"
                    placeholder={selectedCard ? "Cosa emerge osservando questa carta? Libere associazioni, emozioni, metafore..." : "Seleziona prima una carta"}
                />

                <div className="flex justify-between items-center mt-3">
                    <div className="text-xs text-slate-400 font-sans">
                        {reflection.length} caratteri
                    </div>
                    <button
                        onClick={handleSaveReflection}
                        disabled={!selectedCard || !reflection.trim()}
                        className="px-6 py-2 bg-slate-800 text-white rounded-lg font-bold hover:bg-slate-900 transition font-sans border-0 disabled:bg-slate-300 disabled:cursor-not-allowed text-sm"
                    >
                        💾 Salva Riflessione
                    </button>
                </div>
            </div>

            {/* Facilitator Questions */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <h4 className="font-bold text-slate-800 mb-3 text-base font-sans">🎯 Domande Facilitatrici</h4>
                <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-700 font-sans">
                    <div>• Se questa carta potesse parlare, cosa direbbe?</div>
                    <div>• Quale parte della tua vita rispecchia?</div>
                    <div>• Cosa vorresti aggiungere o togliere?</div>
                    <div>• Come ti senti dopo averla osservata?</div>
                    <div>• Cosa ti ricorda questa carta?</div>
                    <div>• Che emozione ti evoca?</div>
                </div>
            </div>

            {/* Fullscreen Image Modal */}
            {fullscreenImage && (
                <div
                    className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4 animate-fadeIn"
                    onClick={() => setFullscreenImage(null)}
                >
                    <div className="relative max-w-6xl max-h-screen w-full h-full flex items-center justify-center">
                        {/* Close button */}
                        <button
                            className="absolute top-4 right-4 text-white text-4xl font-bold hover:text-slate-300 transition z-10"
                            onClick={() => setFullscreenImage(null)}
                        >
                            ✕
                        </button>

                        {/* Image */}
                        <img
                            src={fullscreenImage.image}
                            alt={fullscreenImage.theme}
                            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />

                        {/* Card Info */}
                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-slate-900 bg-opacity-90 text-white px-6 py-3 rounded-xl">
                            <p className="font-bold text-lg font-sans">{fullscreenImage.theme}</p>
                            <p className="text-sm text-slate-300 font-sans">{fullscreenImage.prompt}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreativeCards;
